import { classNames } from '@fullstory/util/fmt';
import React from 'react';
import { Box } from '../../../Box';
import { RangeColor } from '../../types';
import css from './index.module.scss';

interface RangeSegmentProps {
  /**
   * If `true`, left side of the day will be rounded instead of squared. Useful for
   * days that are at the beginning of a month, week, and/or range.
   */
  start: boolean;
  /**
   * If `true`, right side of the day will be rounded instead of squared. Useful for
   * days that are at the end of a month, week, and/or range.
   */
  end: boolean;
  /**
   * Controls the selected and prominent color of the day.
   */
  color: RangeColor;
  /**
   * If `true`, range segment will be outlined. Useful for showing a selection preview.
   */
  outline?: boolean;
  /**
   * When a range crosses multiple months, the range should fade out and then in to
   * visualize that the range continues into the next month.
   */
  fade?: 'in' | 'out';
}

const RangeSegment: React.FC<RangeSegmentProps> = ({
  start,
  end,
  color,
  outline = false,
  fade,
}) => {
  // do not fade in if range start or out if range end
  const hideFade = (fade === 'in' && start) || (fade === 'out' && end);
  return (
    <Box
      data-component="RangeSegment"
      className={classNames(css.rangeSegment, css[color], fade && css[fade], {
        [css.start]: start,
        [css.end]: end,
        [css.outline]: outline,
        [css.fade]: Boolean(fade && !hideFade),
      })}
    />
  );
};

const MemoizedRangeSegment = React.memo(RangeSegment);

export { MemoizedRangeSegment as RangeSegment };
